
import { Control, Controller, UseFormGetValues, UseFormSetValue, UseFormWatch } from 'react-hook-form'
import { ISocioForm } from './DialogAddEditDadoSocio';
import { Autocomplete, FormControl, FormHelperText, Grid, IconButton, InputAdornment, InputLabel, MenuItem, Select, TextField, Typography } from '@mui/material';
import { formatarCEP, permitirApenasNumerosOuSN } from '../../../../../helpers/masks';
import { useMemo } from 'react';
import { useQuery } from 'react-query';
import axiosClient from '../../../../../lib/axios';
import { desformatarCEP } from '../../../../../helpers/unmask';
import { enqueueSnackbar } from 'notistack';
import { ViaCEPResposta } from '../../../../../types/externas/viacep-resultado';
import SearchIcon from '@mui/icons-material/Search';

export default function DadosConjugeSocio({
    control,
    watch,
    getValues,
    setValue
}: DadosConjugeSocioProps
) {

    const ufs = useMemo(() => ["AC", "AL", "AP", "AM", "BA", "CE", "DF", "ES", "GO", "MA", "MT", "MS", "MG", "PA", "PB", "PR", "PE", "PI", "RJ", "RN", "RS", "RO", "RR", "SC", "SP", "SE", "TO"], [])

    //queries
    const { data } = useQuery({
        queryKey: ["pesquisar-cidades", watch(`uf_conjuge`)],
        queryFn: async () => {
            if (watch(`uf_conjuge`) === undefined) return []
            let result = await axiosClient.get("/pesquisar-cidades", {
                params: {
                    uf: getValues(`uf_conjuge`)
                }
            })
            return result?.data
        }
    });
    const { dataCidades }: ICidades = {
        dataCidades: data || []
    }
    //

    //funções manipuladoras
    const handlePesquisarCep = async () => {
        const { cep_conjuge } = getValues();
        if (cep_conjuge === undefined || cep_conjuge === "") return

        const unmaskCep = desformatarCEP(cep_conjuge)

        if (unmaskCep.length !== 8) {
            enqueueSnackbar('CEP inválido. Deve conter 8 dígitos.', { variant: 'error' })
            return
        }

        try {
            const response = await axiosClient.get<ViaCEPResposta>(`https://viacep.com.br/ws/${unmaskCep}/json/`);
            const { bairro, logradouro, localidade, uf } = response.data
            setValue(`uf_conjuge`, uf, { shouldValidate: true, shouldDirty: true })
            setValue(`bairro_conjuge`, bairro, { shouldValidate: true, shouldDirty: true })
            setValue(`cidade_conjuge`, localidade, { shouldValidate: true, shouldDirty: true })
            setValue(`endereco_conjuge`, logradouro, { shouldValidate: true, shouldDirty: true })
        } catch (error) {
            enqueueSnackbar('Erro ao buscar endereço por CEP: ' + error, { variant: 'error' })
            return
        }
    }

    return (
        <Grid container size={{ xs: 12 }} spacing={2}>
            <Grid size={{ xs: 12 }}>
                <Typography fontWeight={'bold'}>Endereço</Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
                <Controller
                    name={`cep_conjuge`}
                    control={control}
                    rules={{ required: 'Campo obrigatório' }}
                    render={({ field, fieldState: { error, invalid } }) => (
                        <TextField
                            {...field}
                            label="CEP"
                            variant="outlined"
                            fullWidth
                            required
                            onChange={(e) => field.onChange(formatarCEP(e.target.value))}
                            onBlur={handlePesquisarCep}
                            error={invalid}
                            helperText={error ? error.message : ""}
                            slotProps={{
                                input: {
                                    endAdornment: <InputAdornment position="end">
                                        <IconButton
                                            aria-label="toggle password visibility"
                                            onClick={handlePesquisarCep}
                                            edge="end"
                                        >
                                            <SearchIcon />
                                        </IconButton>
                                    </InputAdornment>
                                }
                            }}
                        />
                    )}
                />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
                <Controller
                    name={`endereco_conjuge`}
                    control={control}
                    rules={{ required: 'Campo obrigatório' }}
                    render={({ field, fieldState: { error, invalid } }) => (
                        <TextField
                            {...field}
                            label="Endereço"
                            variant="outlined"
                            required
                            fullWidth
                            error={invalid}
                            helperText={error ? error.message : ""}
                            slotProps={{
                                inputLabel: { shrink: field.value ? true : undefined }
                            }}
                        />
                    )}
                />
            </Grid>

            <Grid size={{ xs: 12, md: 2 }}>
                <Controller
                    name={`numero_conjuge`}
                    control={control}
                    render={({ field, fieldState: { error, invalid } }) => (
                        <TextField
                            {...field}
                            label="Número"
                            type="text"
                            variant="outlined"
                            fullWidth
                            onChange={(e) => {
                                field.onChange(permitirApenasNumerosOuSN(e.target.value))
                            }}
                            error={invalid}
                            helperText={error ? error.message : ""}
                        />
                    )}
                />
            </Grid>

            <Grid size={{ xs: 12, md: 3 }}>
                <Controller
                    name={`complemento_conjuge`}
                    control={control}
                    render={({ field, fieldState: { error, invalid } }) => (
                        <TextField
                            {...field}
                            label="Complemento"
                            variant="outlined"
                            fullWidth
                            error={invalid}
                            helperText={error ? error.message : ""}
                        />
                    )}
                />
            </Grid>


            <Grid size={{ xs: 12, md: 3 }}>
                <Controller
                    name={`bairro_conjuge`}
                    control={control}
                    rules={{ required: 'Campo obrigatório' }}
                    render={({ field, fieldState: { error, invalid } }) => (
                        <TextField
                            {...field}
                            label="Bairro"
                            variant="outlined"
                            required
                            fullWidth
                            error={invalid}
                            helperText={error ? error.message : ""}
                            slotProps={{
                                inputLabel: { shrink: field.value ? true : undefined }
                            }}
                        />
                    )}
                />
            </Grid>


            <Grid size={{ xs: 12, md: 2 }}>
                <Controller
                    name={`uf_conjuge`}
                    control={control}
                    rules={{ required: 'Campo obrigatório' }}
                    render={({ field, fieldState: { error, invalid } }) => (
                        <FormControl fullWidth error={invalid} required>
                            <InputLabel id="uf-conjuge-socio-select">UF</InputLabel>
                            <Select
                                labelId="uf-conjuge-socio-select"
                                value={field.value || ''}
                                label="UF"
                                onChange={(e) => field.onChange(e.target.value)}
                            >
                                {ufs.map((uf, index) => (
                                    <MenuItem key={index} value={uf}>{uf}</MenuItem>
                                ))}
                            </Select>
                            {error && (
                                <FormHelperText>{error.message}</FormHelperText>
                            )}
                        </FormControl>
                    )}
                />
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
                <Controller
                    name={`cidade_conjuge`}
                    control={control}
                    rules={{ required: 'Campo obrigatório' }}
                    render={({ field, fieldState: { error, invalid } }) => (
                        <Autocomplete
                            value={field.value || null}
                            fullWidth
                            disablePortal
                            options={dataCidades}
                            onChange={(_, v) => field.onChange(v || '')}
                            renderInput={(params) =>
                                <TextField
                                    {...params}
                                    label="Cidade"
                                    required
                                    error={invalid}
                                    helperText={error ? error.message : ""}
                                />
                            }
                        />
                    )}
                />
            </Grid>
        </Grid>
    )
}

interface DadosConjugeSocioProps {
    control: Control<ISocioForm, any>;
    watch: UseFormWatch<ISocioForm>;
    getValues: UseFormGetValues<ISocioForm>
    setValue: UseFormSetValue<ISocioForm>;
}

interface ICidades {
    dataCidades: Array<string>
}
